import type { KeyboardEvent } from 'react';

/**
 * Shared keydown handler for wizard step forms.
 *
 * When Enter is pressed on a text/number/date input, prevents form submission
 * and advances focus to the next focusable element (like Tab).
 *
 * Preserves native Enter behaviour on buttons, selects, textareas,
 * radios, checkboxes, and summary elements.
 */
export function wizardFormKeyDown(e: KeyboardEvent<HTMLFormElement>): void {
  if (e.key !== 'Enter') return;

  const target = e.target as HTMLElement;
  const tag = target.tagName;
  const type = (target as HTMLInputElement).type;

  // Allow native Enter behaviour on these element types
  const allowNative =
    tag === 'BUTTON' ||
    tag === 'SELECT' ||
    tag === 'TEXTAREA' ||
    tag === 'SUMMARY' ||
    (tag === 'INPUT' && (type === 'radio' || type === 'checkbox' || type === 'submit'));

  if (allowNative) return;

  e.preventDefault();

  // Advance focus to the next focusable element within the form
  const focusable = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    )
  );
  const idx = focusable.indexOf(target);
  if (idx !== -1 && idx < focusable.length - 1) {
    focusable[idx + 1].focus();
  }
}
